import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ColorPickerDirective, ColorPickerModule } from 'ngx-color-picker';
import { ICON_COLOR_OPTIONS, ICON_OPTIONS } from 'app/shared/constants/lists.constant';
import { ConfirmDialogComponent } from '../company-form-config/modals-config/confirm-dialog.component';
import { FieldGroup } from './field-group.interface';
import { firstFreePosition } from './field-groups.util';

export interface FieldGroupsDialogData {
  groups: FieldGroup[];
  fieldCountByGroupId: Record<string, number>;
}

function createGroupId(): string {
  const hex = '0123456789abcdef';
  let id = '';
  for (let i = 0; i < 24; i++) {
    id += hex[Math.floor(Math.random() * 16)];
  }
  return id;
}

@Component({
  selector: 'app-field-groups-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatTooltipModule,
    ColorPickerModule,
  ],
  templateUrl: './field-groups-dialog.component.html',
})
export class FieldGroupsDialogComponent {
  readonly availableIcons = ICON_OPTIONS;
  readonly availableColors = ICON_COLOR_OPTIONS;
  readonly CUSTOM_COLOR = '__custom__';
  readonly MIN_POSITION = 1;

  iconSearchControl = new FormControl('');
  filteredIcons$: Observable<string[]>;

  groups: FieldGroup[] = [];
  removedGroupIds: string[] = [];
  newGroupNameControl = new FormControl('');
  errorMessage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<FieldGroupsDialogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: FieldGroupsDialogData
  ) {
    this.groups = (data.groups || []).map((group) => ({ ...group }));

    this.filteredIcons$ = this.iconSearchControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const search = (value || '').toLowerCase();
        return this.availableIcons.filter((icon) => icon.toLowerCase().includes(search));
      })
    );
  }

  selectIcon(group: FieldGroup, icon: string | null): void {
    group.icon = icon;
    this.iconSearchControl.setValue('');
  }

  private isDuplicateName(name: string, ignoreId?: string): boolean {
    const key = name.trim().toLowerCase();
    return this.groups.some(
      (group) => group._id !== ignoreId && group.name.trim().toLowerCase() === key
    );
  }

  private nextPosition(): number {
    return firstFreePosition(this.groups.map((group) => group.position));
  }

  isPresetColor(color: string | null): boolean {
    return !!color && this.availableColors.some((option) => option.value === color);
  }

  onColorSelect(
    group: FieldGroup,
    value: string | null,
    select: MatSelect,
    picker: ColorPickerDirective
  ): void {
    if (value === this.CUSTOM_COLOR) {
      // El centinela no debe quedar como valor del select; si no, cancelar el picker lo deja pegado
      select.value = group.iconColor;
      picker.openDialog();
      return;
    }
    group.iconColor = value;
  }

  onCustomColorPicked(group: FieldGroup, color: string): void {
    group.iconColor = (color || '').toUpperCase() || null;
  }

  isPositionInvalid(group: FieldGroup): boolean {
    return !(Number(group.position) >= this.MIN_POSITION);
  }

  addGroup(): void {
    const name = this.newGroupNameControl.value?.trim();
    if (!name) return;

    if (this.isDuplicateName(name)) {
      this.errorMessage = `Ya existe un grupo llamado "${name}".`;
      return;
    }

    this.groups.push({
      _id: createGroupId(),
      name,
      position: this.nextPosition(),
      icon: null,
      iconColor: null,
    });
    this.newGroupNameControl.setValue('');
    this.errorMessage = null;
  }

  onNameChange(group: FieldGroup, value: string): void {
    const name = (value || '').trim();
    if (name && this.isDuplicateName(name, group._id)) {
      this.errorMessage = `Ya existe un grupo llamado "${name}".`;
      return;
    }
    group.name = value;
    this.errorMessage = null;
  }

  positionOccupant(group: FieldGroup): FieldGroup | null {
    return (
      this.groups.find(
        (other) =>
          other._id !== group._id && Number(other.position) === Number(group.position)
      ) ?? null
    );
  }

  onPositionChange(group: FieldGroup, value: string): void {
    const position = Number(value);
    group.position = Number.isFinite(position) ? position : this.MIN_POSITION;
  }

  private shiftPositionsFrom(group: FieldGroup): void {
    this.groups.forEach((other) => {
      if (other._id === group._id) return;
      const position = Number(other.position) || 0;
      if (position < Number(group.position)) return;
      other.position = position + 1;
    });
  }

  fieldCount(group: FieldGroup): number {
    return this.data.fieldCountByGroupId?.[group._id] ?? 0;
  }

  removeGroup(group: FieldGroup): void {
    const count = this.fieldCount(group);

    if (!count) {
      this.applyRemoval(group);
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        width: '460px',
        data: {
          title: 'Eliminar grupo',
          message:
            `El grupo "${group.name}" tiene ${count} campo(s) asignado(s).\n\n` +
            'Si continúas, esos campos quedarán sin grupo.',
          confirmText: 'Eliminar',
          confirmColor: 'warn',
        },
        panelClass: 'responsive-modal',
      })
      .afterClosed()
      .subscribe((confirmed) => confirmed && this.applyRemoval(group));
  }

  private applyRemoval(group: FieldGroup): void {
    this.groups = this.groups.filter((other) => other._id !== group._id);
    this.removedGroupIds.push(group._id);
    this.errorMessage = null;
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    const emptyName = this.groups.find((group) => !group.name?.trim());
    if (emptyName) {
      this.errorMessage = 'Todos los grupos deben tener nombre.';
      return;
    }

    if (this.groups.some((group) => this.isPositionInvalid(group))) {
      this.errorMessage = 'La posición debe ser 1 o mayor.';
      return;
    }

    const duplicate = this.groups.find((group) => this.isDuplicateName(group.name, group._id));
    if (duplicate) {
      this.errorMessage = `Ya existe un grupo llamado "${duplicate.name.trim()}".`;
      return;
    }

    const collided = this.groups.find((group) => this.positionOccupant(group));
    if (collided) {
      const occupant = this.positionOccupant(collided);
      this.dialog
        .open(ConfirmDialogComponent, {
          width: '460px',
          data: {
            title: 'Posición ocupada',
            message:
              `La posición ${collided.position} ya está ocupada por "${occupant.name}".\n\n` +
              'Si continúas, ese grupo y los siguientes se recorrerán una posición.',
            confirmText: 'Recorrer y guardar',
            confirmColor: 'primary',
          },
          panelClass: 'responsive-modal',
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (!confirmed) return;
          this.shiftPositionsFrom(collided);
          this.closeWithResult();
        });
      return;
    }

    this.closeWithResult();
  }

  private closeWithResult(): void {
    this.dialogRef.close({
      groups: this.groups.map((group) => ({ ...group, name: group.name.trim() })),
      removedGroupIds: this.removedGroupIds,
    });
  }
}
